import _ from 'lodash'

/**
 * Service para cálculos de preço de propostas de plano
 * Centraliza toda lógica de pricing e aplicação de descontos
 */
class PriceCalculator {
    /**
     * Calcula o preço total da proposta
     * @param {Object} planPackage - Pacote de plano selecionado
     * @param {Array} selectedModules - Módulos selecionados
     * @returns {Number} Preço total
     */
    calculateTotalPrice(planPackage, selectedModules = []) {
        if (!planPackage || !planPackage.price) {
            return 0
        }

        let totalPrice = parseFloat(planPackage.price)

        selectedModules.forEach(module => {
            if (!module.checked) return

            const price = parseFloat(module.price) || 0
            const quantity = parseInt(module.add_amount) || 0
            totalPrice += price * quantity
        })

        return totalPrice
    }

    /**
     * Calcula o desconto aplicável
     * @param {Number} totalPrice - Preço total antes do desconto
     * @param {Object} planPackage - Pacote de plano
     * @param {Boolean} loyalty - Se tem fidelidade ativada
     * @param {Object} discountCode - Código de desconto aplicado
     * @returns {Number} Valor do desconto
     */
    calculateDiscount(
        totalPrice,
        planPackage,
        loyalty = false,
        discountCode = null,
    ) {
        let discount = 0

        // Prioridade 1: Código de desconto
        if (
            discountCode &&
            discountCode.discount_price &&
            discountCode.discount_price > 0
        ) {
            discount = discountCode.discount_price
        }
        // Prioridade 2: Desconto por fidelidade
        else if (
            loyalty &&
            planPackage &&
            planPackage.default_anual_discount > 0
        ) {
            discount = totalPrice * planPackage.default_anual_discount * 0.01
        }

        return discount
    }

    /**
     * Calcula o preço final após aplicar desconto
     * @param {Number} totalPrice - Preço total
     * @param {Number} discount - Desconto a aplicar
     * @returns {Number} Preço final
     */
    calculateFinalPrice(totalPrice, discount = 0) {
        return discount > 0 ? totalPrice - discount : totalPrice
    }

    /**
     * Calcula taxa de configuração
     * @param {Object} planPackage - Pacote de plano
     * @param {Number} finalPrice - Preço final da proposta
     * @param {Boolean} loyalty - Se tem fidelidade
     * @param {Boolean} renovation - Se é renovação
     * @param {Boolean} configuration - Se cobra configuração
     * @returns {Number} Taxa de configuração
     */
    calculateConfigPrice(
        planPackage,
        finalPrice,
        loyalty = false,
        renovation = true,
        configuration = true,
    ) {
        if (!planPackage || !planPackage.config_price) {
            return 0
        }

        // Não cobra taxa se tiver fidelidade
        if (loyalty) {
            return 0
        }

        // Só cobra se for renovação e configuração estiver ativa
        if (!renovation || !configuration) {
            return 0
        }

        const configPrice = planPackage.config_price - finalPrice

        return configPrice > 0 ? configPrice : 0
    }

    /**
     * Calcula todos os preços de uma vez
     * @param {Object} params - Parâmetros para cálculo
     * @returns {Object} Objeto com todos os preços calculados
     */
    calculateAll({
        planPackage,
        selectedModules = [],
        loyalty = false,
        renovation = true,
        configuration = true,
        discountCode = null,
    }) {
        const totalPrice = this.calculateTotalPrice(
            planPackage,
            selectedModules,
        )
        const discount = this.calculateDiscount(
            totalPrice,
            planPackage,
            loyalty,
            discountCode,
        )
        const finalPrice = this.calculateFinalPrice(totalPrice, discount)
        const configPrice = this.calculateConfigPrice(
            planPackage,
            finalPrice,
            loyalty,
            renovation,
            configuration,
        )

        return {
            totalPrice,
            discount,
            finalPrice,
            configPrice,
        }
    }

    /**
     * Cria uma versão com debounce dos métodos de cálculo
     * @param {Number} wait - Tempo de espera em ms (padrão: 300ms)
     * @returns {Object} Objeto com métodos debounced
     */
    createDebounced(wait = 300) {
        return {
            calculateTotalPrice: _.debounce(
                this.calculateTotalPrice.bind(this),
                wait,
            ),
            calculateDiscount: _.debounce(
                this.calculateDiscount.bind(this),
                wait,
            ),
            calculateFinalPrice: _.debounce(
                this.calculateFinalPrice.bind(this),
                wait,
            ),
            calculateConfigPrice: _.debounce(
                this.calculateConfigPrice.bind(this),
                wait,
            ),
            calculateAll: _.debounce(this.calculateAll.bind(this), wait),
        }
    }
}

// Exportar instância singleton
const priceCalculator = new PriceCalculator()

export default priceCalculator

// Exportar também a classe para testes
export { PriceCalculator }
